import { useEffect, useState, useMemo } from 'react';
import api, { fileUrl } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, TriangleAlert, ShieldCheck, EyeOff, Eye, Repeat, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function AuditView() {
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');

  const load = () => Promise.all([api.get('/audit/captures'), api.get('/campaigns')])
    .then(([a, c]) => { setData(a.data); setCampaigns(c.data); });
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.rows;
    if (filter === 'mismatch') r = r.filter((x) => x.mismatch);
    else if (filter === 'auto') r = r.filter((x) => x.auto_swapped);
    else if (filter === 'invalid') r = r.filter((x) => x.invalid);
    if (q.trim()) {
      const s = q.toLowerCase();
      r = r.filter((x) => [x.society, x.tower, x.lift, x.wall, x.captured_client, x.expected_client, x.campaign, x.operator].join(' ').toLowerCase().includes(s));
    }
    return r;
  }, [data, filter, q]);

  const invalidate = async (row) => {
    setBusy(row.proof_id);
    try { await api.post(`/proofs/${row.proof_id}/validity`, { invalid: !row.invalid }); toast.success(row.invalid ? 'Restored' : 'Hidden from client reports'); await load(); }
    catch { toast.error('Action failed'); } finally { setBusy(''); }
  };
  const reassign = async (row, campaign_id) => {
    const c = campaigns.find((x) => x.id === campaign_id);
    setBusy(row.proof_id);
    try { await api.patch(`/proofs/${row.proof_id}/correct`, { campaign_id, client_name: c ? c.client_name : null }); toast.success('Reassigned'); await load(); }
    catch { toast.error('Reassign failed'); } finally { setBusy(''); }
  };

  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Data Audit</p>
        <h1 className="font-head text-3xl font-black">Capture Audit & Corrections</h1>
        <p className="text-sm text-muted-foreground">Every capture with its tagged client vs the wall's expected occupant. Nothing is deleted — hide moves a photo out of client reports; reassign fixes the client.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Total captures', s.total, 'text-foreground', 'all'],
          ['Mismatches', s.mismatches, 'text-rose-500', 'mismatch'],
          ['Auto-swapped', s.auto_swaps, 'text-amber-500', 'auto'],
          ['Hidden (invalid)', s.invalid, 'text-slate-400', 'invalid']].map(([label, val, cls, f]) => (
          <button key={label} data-testid={`audit-card-${f}`} onClick={() => setFilter(f)} className={`rounded-xl border p-4 text-left transition ${filter === f ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`font-head text-3xl font-black ${cls}`}>{val}</p>
          </button>
        ))}
      </div>

      {data.by_client.some((c) => c.mismatched > 0) && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-bold text-rose-500"><TriangleAlert className="h-4 w-4" /> Clients with mismatched photos</p>
          <div className="flex flex-wrap gap-2">
            {data.by_client.filter((c) => c.mismatched > 0).map((c) => (
              <span key={c.client} className="rounded-full border border-rose-500/50 px-3 py-1 text-xs">{c.client}: <b>{c.mismatched}</b> mismatch / {c.captured} total</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search society, client, operator…" className="pl-8" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="audit-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All captures</SelectItem>
            <SelectItem value="mismatch">Mismatches only</SelectItem>
            <SelectItem value="auto">Auto-swapped</SelectItem>
            <SelectItem value="invalid">Hidden</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{rows.length} shown</span>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.proof_id} data-testid={`audit-row-${r.proof_id}`} className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center ${r.invalid ? 'border-slate-700 opacity-60' : r.mismatch ? 'border-rose-500/60 bg-rose-500/5' : 'border-border bg-card'}`}>
            <img src={fileUrl(r.thumb)} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{r.society} · {r.tower} · {r.lift} · {r.wall}</span>
                {r.mismatch && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Mismatch</span>}
                {r.auto_swapped && <span className="rounded-full border border-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-500">Auto-swap</span>}
                {r.invalid && <span className="rounded-full border border-slate-500 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">Hidden</span>}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Tagged: <b className={r.mismatch ? 'text-rose-500' : 'text-foreground'}>{r.captured_client}</b>
                {r.mismatch && <> · Wall's expected: <b className="text-foreground">{r.expected_client}</b></>}
                {' · '}{r.action} · {(r.timestamp || '').slice(0, 16).replace('T', ' ')} · {r.operator}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Select onValueChange={(v) => reassign(r, v)}>
                <SelectTrigger data-testid={`audit-reassign-${r.proof_id}`} className="h-8 w-36 text-xs"><Repeat className="mr-1 h-3 w-3" /><span className="truncate">Reassign client</span></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_name} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" data-testid={`audit-hide-${r.proof_id}`} disabled={busy === r.proof_id} onClick={() => invalidate(r)}
                className={r.invalid ? 'border-emerald-700 text-emerald-500' : 'border-slate-600 text-slate-400'}>
                {busy === r.proof_id ? <Loader2 className="h-4 w-4 animate-spin" /> : r.invalid ? <><Eye className="mr-1 h-3.5 w-3.5" />Restore</> : <><EyeOff className="mr-1 h-3.5 w-3.5" />Hide</>}
              </Button>
            </div>
          </div>
        ))}
        {!rows.length && <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground"><ShieldCheck className="mx-auto mb-2 h-8 w-8" />No captures match this filter.</div>}
      </div>
    </div>
  );
}
